import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { IFileDetails } from "../../interface/files";
import ImageUploadWithGallery from "./imageUploadWithGallery";
import { AppDispatch, RootState } from "../../store";
import {
  fetchPropertyImages,
  uploadPropertyImageFile,
  deletePropertyImages,
  resetFileUploadState,
  resteDeleteFileState,
} from "../../store/slice/propertyImagesSlice";
import { Status, Messages } from "../../utils/constants";
import { REIButton, REIToastContainer } from "../ui";

interface Props {
  propertyData: string | null;
}

const BasicFileUpload: React.FC<Props> = ({ propertyData }) => {
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshData, setRefreshData] = useState(true);
  const [propertyImages, setPropertyImages] = useState<IFileDetails[]>([]);
  const toastRef = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);
  const dispatch: AppDispatch = useDispatch();
  const fetchPropertyImagesState = useSelector((state: RootState) => state.propertyImages.fetchPropertyImages);
  const uploadPropertyImagesState = useSelector((state: RootState) => state.propertyImages.uploadPropertyImageResponse);
  const deletePropertyImagesState = useSelector(
    (state: RootState) => state.propertyImages.deletePropertyImagesResponse
  );
  const watchListID = useRef<string | null>();

  useEffect(() => {
    watchListID.current = propertyData;
  }, [propertyData]);

  useEffect(() => {
    if (propertyData) {
      setLoading(true);

      dispatch(fetchPropertyImages({ WatchlistID: propertyData || "", isAttachementData: false }));
    }
    setLoading(false);
  }, [dispatch, watchListID.current, refreshData]);

  useEffect(() => {
    if (fetchPropertyImagesState.status === Status.SUCCESS && fetchPropertyImagesState.error === "") {
      setPropertyImages(fetchPropertyImagesState.data);
      setLoading(false);
    } else if (fetchPropertyImagesState.status === Status.FAILED) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: Messages.ERROR,
        life: 2000,
      });
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [fetchPropertyImagesState.data, fetchPropertyImagesState.error]);

  useEffect(() => {
    if (uploadPropertyImagesState.status === Status.SUCCESS && uploadPropertyImagesState.error === "") {
      setLoading(false);
      setTimeout(() => {
        setTotalSize(0);
        toastRef.current?.show({
          severity: "success",
          summary: Messages.SUCCESS,
          detail: Messages.FILEUPLOAD_SUCCESS,
          life: 2000,
        });
        dispatch(resetFileUploadState());
        setRefreshData(!refreshData);
        setLoading(false);
      }, 1000);
    }

    if (uploadPropertyImagesState.error !== "") {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: uploadPropertyImagesState.error,
        life: 2000,
      });

      dispatch(resetFileUploadState());
      setRefreshData(!refreshData);
      setTotalSize(0);
      fileUploadRef.current?.clear();
      setLoading(false);
    }
  }, [uploadPropertyImagesState.data, uploadPropertyImagesState.error]);

  useEffect(() => {
    if (deletePropertyImagesState.status === Status.SUCCESS && deletePropertyImagesState.error === "") {
      toastRef.current?.show({
        severity: "success",
        summary: Messages.SUCCESS,
        detail: Messages.FILEDELETE_SUCCESS,
        life: 2000,
      });
      dispatch(resteDeleteFileState());
      setRefreshData(!refreshData);
      setLoading(false);
    }

    if (deletePropertyImagesState.error !== "" && deletePropertyImagesState.status === Status.FAILED) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: deletePropertyImagesState.error,
        life: 3000,
      });
      dispatch(resteDeleteFileState());
      setRefreshData(!refreshData);
      setLoading(false);
    }
  }, [deletePropertyImagesState.data]);

  const deleteSelectedFile = async (fileName: string) => {
    setLoading(true);
    dispatch(
      deletePropertyImages({
        FileName: fileName,
        WatchlistID: watchListID.current ?? "",
      })
    );
  };

  const fileUploadHandler = async ({ files }) => {
    if (!files?.length) return;
    const [file] = files;
    setLoading(true);
    dispatch(uploadPropertyImageFile({ file, memberPropertyId: propertyData || "", isAttachment: 0 }));
  };

  const onTemplateSelect = (e) => {
    let _totalSize = totalSize;
    e.files.forEach((file) => (_totalSize += file.size));
    setTotalSize(_totalSize);
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const onCancel = () => {
    fileUploadRef.current?.clear();
  };

  const basicItemTemplate = (item: IFileDetails) => (
    <div className="flex flex-col content-center">
      <img src={item.FileUrl} alt={item.FileName} className="w-full h-[156px]" />
      <REIButton
        icon="pi pi-times"
        classNames="p-button-rounded p-button-danger p-button-sm"
        onClick={() => deleteSelectedFile(item.FileName)}
        loading={loading}
        disabled={loading}
        tooltip="Remove"
      />
    </div>
  );

  const emptyTemplate = () => <div className="flex place-content-center flex-column">No Photos</div>;

  return (
    <>
      <REIToastContainer toastReference={toastRef} />
      <ImageUploadWithGallery
        isAutoUpload={true}
        loading={loading}
        emptyTemplate={emptyTemplate}
        fileData={propertyImages}
        uploadHandler={fileUploadHandler}
        onCancel={onCancel}
        onSelect={onTemplateSelect}
        onError={onTemplateClear}
        onClear={onTemplateClear}
        chooseOptions={{
          label: ` ${totalSize > 0 ? "Browse" : "Upload"} `,
          iconOnly: false,
          icon: "",
          className: `custom-choose-btn p-button-rounded p-button-outlined text-sm small-icon ${
            loading ? "opacity-50 pointer-events-none" : ""
          }`,
        }}
        loading={loading}
        itemTemplate={basicItemTemplate}
      />
    </>
  );
};

export default BasicFileUpload;
